import { useState } from "react";
import { Transition } from "@headlessui/react";
import { FiEye } from "react-icons/fi";
import UserForm from "./UserForm";

export default function UserList({
  users,
  setUsers,
  stagedUsers,
  setStagedUsers,
  accounts,
  unions,
  setDialog,
  navigate,
  isAddingUser,
  setIsAddingUser,
  currentUserPage,
  setCurrentUserPage,
}) {
  const [userFilter, setUserFilter] = useState("all");
  const [usersPerPage] = useState(10);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const getFilteredUsers = () => {
    const activeUsers = stagedUsers.filter((user) => user.active);
    let filteredUsers;
    switch (userFilter) {
      case "non-temp":
        filteredUsers = activeUsers.filter((user) => !user.isTemporary);
        break;
      case "all":
      default:
        filteredUsers = activeUsers;
    }
    const indexOfLastUser = currentUserPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return {
      users: filteredUsers.slice(indexOfFirstUser, indexOfLastUser),
      totalUsers: filteredUsers.length,
    };
  };

  const handleViewUser = (userId) => navigate(`/users/${userId}`);

  const Paginator = ({
    currentPage,
    totalItems,
    itemsPerPage,
    onPageChange,
  }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxPagesToShow = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      if (endPage - startPage + 1 < maxPagesToShow)
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      return pages;
    };

    return (
      <div className="flex justify-center items-center gap-2 mt-6">
        <button
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-2xl text-sm font-semibold transition-all ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-400 hover:text-indigo-600"
          }`}
        >
          Previous
        </button>
        {getPageNumbers().map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`w-9 h-9 rounded-2xl text-sm font-semibold transition-all ${
              currentPage === page
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-400 hover:text-indigo-600"
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() =>
            currentPage < totalPages && onPageChange(currentPage + 1)
          }
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-2xl text-sm font-semibold transition-all ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-400 hover:text-indigo-600"
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  const { users: pagedUsers, totalUsers } = getFilteredUsers();

  return (
    <>
      {/* Header row */}

      {/* Table */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              {["Name", "Role", "Status", "Last Logged In", "Actions"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-8 py-5 text-left text-sm font-semibold text-gray-600 uppercase tracking-widest"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {pagedUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-8 py-16 text-center">
                  <div className="text-gray-400 text-xl">No users found</div>
                  <p className="text-sm text-gray-500 mt-2">
                    Try adjusting your filter
                  </p>
                </td>
              </tr>
            ) : (
              pagedUsers.map((user) => (
                <tr
                  key={user._id}
                  onClick={() => handleViewUser(user._id)}
                  className="hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <td className="px-8 py-6">
                    <div className="text-base font-semibold text-gray-900">
                      {user.displayName}
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize">
                      {user.role || "Unassigned"}
                    </span>
                  </td>
                  <td className="px-8 py-6">
                    {user.active ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-6 text-sm text-gray-500">
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "Never"}
                  </td>
                  <td className="px-8 py-6">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewUser(user._id);
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 hover:border-indigo-500 hover:text-indigo-600 text-gray-700 rounded-2xl text-sm font-medium transition-all active:scale-95"
                    >
                      <FiEye className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Paginator
        currentPage={currentUserPage}
        totalItems={totalUsers}
        itemsPerPage={usersPerPage}
        onPageChange={setCurrentUserPage}
      />

      <Transition
        show={isAddingUser || isEditingUser}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <UserForm
          isEditing={isEditingUser}
          user={isEditingUser ? editingUser : null}
          setIsEditing={setIsEditingUser}
          setIsAdding={setIsAddingUser}
          accounts={accounts}
          unions={unions}
          setUsers={setUsers}
          setStagedUsers={setStagedUsers}
          setDialog={setDialog}
        />
      </Transition>
    </>
  );
}
